import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { ConsultationSelect } from "@components/ConsultationSelect";

import { formatDateToString } from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MAX_PAGE_SIZE,
  MEGABYTE,
} from "@constants/index";

import { useCreateImmunization } from "@hooks/mutations/immunization/useCreateImmunization";
import { useGetConsultationsForPatient } from "@hooks/queries/consultation/useGetConsultationsForPatient";
import { useGetSingleConsultation } from "@hooks/queries/consultation/useGetSingleConsultation";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { useAuth } from "@contexts/auth";

import { Immunization } from "@customTypes/immunization";


interface AddModalProps {
  opened: boolean;
  close: () => void;
}

export const AddModal = ({ opened, close }: AddModalProps) => {
  const { t, i18n } = useTranslation();
  const { patientId } = useParams();

  const { id: userId, fullName } = useAuth();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [consultationId, setConsultationId] = useState<string | null>(null);
  const [immunizationDate, setImmunizationDate] = useState<Date | null>(
    new Date()
  );
  const [formData, setFormData] = useState({} as Immunization);
  const [doctor, setDoctor] = useState<ComboboxItem | null>({
    label: fullName,
    value: userId,
  });
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>();

  const {
    data: consultationsData,
    isLoading: isConsultationsLoading,
    isRefetching: isConsultationsRefetching,
    isSuccess: isConsultationsSuccess,
  } = useGetConsultationsForPatient({
    patientId: patientId as string,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const { mutate: createImmunization, isLoading: isCreateImmLoading } =
    useCreateImmunization();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({ permission: "preventative.create" });
  const { data: selectedConsultationData } = useGetSingleConsultation({
    consultationId: consultationId ?? "",
    isEnabled: !!consultationId,
  });
  const {
    data: supportStaffData,
    isLoading: isSupportStaffLoading,
    isRefetching: isSupportStaffRefetching,
  } = useGetUsers({ permission: "preventative.edit" });

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);
  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);
  const consultationsComboboxItems = useMemo(() => {
    return consultationsData?.newData?.map(item => ({
      label: item.cId,
      value: item.id,
      date: `${formatDateToString(item.date)}, ${item.date.toLocaleTimeString(
        i18n.language,
        { hour: "2-digit", minute: "2-digit" }
      )}`,
    }));
  }, [consultationsData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleCreateImmunization = () => {
    const jsFormData = new FormData();

    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });
    jsFormData.append("date", immunizationDate?.toISOString() ?? "");

    jsFormData.append("consultation_id", formData?.consultationId ?? "");
    jsFormData.append("patient_id", patientId as string);
    jsFormData.append("doses", formData.doses);
    jsFormData.append(
      "administered_by",
      doctor?.value === userId ? userId : doctor?.value ?? ""
    );
    jsFormData.append("aid", supportStaff?.value ?? "");
    jsFormData.append("fast_consultation", String(false));
    jsFormData.append("medication_name", formData.medication);
    jsFormData.append("reason", formData?.reason ?? "");

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    createImmunization(jsFormData, {
      onSuccess: () => {
        setFormData({} as Immunization);
        setConsultationId(null);
        setSupportStaff(null);
        setUploadedFiles([]);
        setImmunizationDate(new Date());
        close();
      },
    });
  };

  useEffect(() => {
    if (consultationId) {
      setFormData(prevData => ({
        ...prevData,
        consultationId,
      }));
    }
  }, [consultationId]);

  useEffect(() => {
    if (selectedConsultationData) {
      setDoctor({
        label: selectedConsultationData.physician.fullName,
        value: selectedConsultationData.physician.id,
      });
    }
  }, [selectedConsultationData]);

  useEffect(() => {
    if (formData) console.log("[Create immunization formData]", formData);
  }, [formData]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-immunization")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              {isConsultationsSuccess && !consultationsData?.newData?.length ? (
                <Alert
                  // w={"fit-content"}
                  variant="light"
                  color="blue"
                  icon={<IconInfoCircle />}>
                  <Text fz={14} lh={1.5} c="blue">
                    {t("empty-consultations")}
                  </Text>
                </Alert>
              ) : null}
              <div className="form-field">
                <ConsultationSelect
                  label={t("consultation")}
                  placeholder={t("select-consultation")}
                  isLoading={
                    isConsultationsLoading || isConsultationsRefetching
                  }
                  disabled={
                    !consultationsData?.newData?.length &&
                    isConsultationsSuccess
                  }
                  data={
                    !consultationsComboboxItems?.length
                      ? []
                      : consultationsComboboxItems
                  }
                  value={formData?.consultationId}
                  setValue={setConsultationId}
                />
              </div>

              <div className="form-field">
                <label htmlFor="medication">
                  {t("medication")}
                  <sup>*</sup>
                </label>
                <input
                  id="medication"
                  type="text"
                  placeholder={t("eg", { example: "Moderna" })}
                  name="medication"
                  value={formData.medication ?? ""}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("support-staff")}
                  placeholder={t("select-support-staff")}
                  data={supportStaffComboboxItems || []}
                  value={supportStaff?.value ?? null}
                  onChange={(_val, option) => setSupportStaff(option)}
                  rightSection={
                    isSupportStaffLoading || isSupportStaffRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                  searchable
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <DatePickerInput
                    label={t("immunization-date")}
                    value={immunizationDate}
                    onChange={setImmunizationDate}
                    leftSection={<IconCalendar size={18} stroke={1.5} />}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="doses">
                    {t("dosage")}
                    <sup>*</sup>
                  </label>
                  <input
                    type="text"
                    // placeholder="Eg: 2/2"
                    id="doses"
                    name="doses"
                    value={formData.doses ?? ""}
                    onChange={e => handleOnChange(e, setFormData, formData)}
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="reason">{t("reason")}</label>
                <textarea
                  rows={5}
                  placeholder={t("type-reason")}
                  id="reason"
                  name="reason"
                  value={formData.reason ?? ""}
                  onChange={e =>
                    handleOnChange(e, setFormData, formData)
                  }></textarea>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto">
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            color="red"
                            variant="outline"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt="xl" pb={"md"}>
          <PermissionGate permission="preventative.create"><Button
            onClick={handleCreateImmunization}
            loading={isCreateImmLoading}
            disabled={
              !formData?.consultationId ||
              !formData?.medication ||
              !formData.doses
            }>
            {t("add-immunization")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
